import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aceDaBrainCatalog } from '@/mocks/catalog';

type Section = 'aliases' | 'produced' | 'remixes';

interface SearchResult {
  item: string;
  section: Section;
  sectionLabel: string;
  sectionIcon: string;
}

export default function CatalogPage() {
  const [activeSection, setActiveSection] = useState<Section>('aliases');
  const [search, setSearch] = useState('');

  const sections: { key: Section; label: string; icon: string; data: string[] }[] = [
    {
      key: 'aliases',
      label: 'Andreas Schmidt Aliases',
      icon: 'ri-user-star-line',
      data: aceDaBrainCatalog.aliases,
    },
    {
      key: 'produced',
      label: 'Clients Produced',
      icon: 'ri-music-2-line',
      data: aceDaBrainCatalog.producedFor,
    },
    {
      key: 'remixes',
      label: 'Remixes produced For',
      icon: 'ri-loop-right-line',
      data: aceDaBrainCatalog.remixesFor,
    },
  ];

  const active = sections.find(s => s.key === activeSection)!;
  const isSearching = search.trim().length > 0;

  const allSearchResults: SearchResult[] = isSearching
    ? sections.flatMap(s =>
        s.data
          .filter(item => item.toLowerCase().includes(search.toLowerCase()))
          .map(item => ({ item, section: s.key, sectionLabel: s.label, sectionIcon: s.icon }))
      )
    : [];

  const filtered = isSearching
    ? active.data.filter(item => item.toLowerCase().includes(search.toLowerCase()))
    : active.data;

  const totalResults = allSearchResults.length;

  return (
    <div className="bg-[#0a0a0a] min-h-screen relative">
      {/* Background image */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img
          src="https://storage.readdy-site.link/project_files/5bc8e699-d202-4ec0-adea-cc7d9ecf40c0/00d6dc9b-f9f5-4277-97b1-5ca13ae92d6c_ChatGPT-Image-23.-Apr.-2026-22_08_13.png?v=6642ccefa706d387907ec260ea8b5102"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[#0a0a0a]/60" />
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black to-transparent" />
      </div>

      {/* Top bar */}
      <div className="fixed top-0 left-0 w-full z-50 bg-[#000000] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/5bc8e699-d202-4ec0-adea-cc7d9ecf40c0/cebdc8d2-642a-4486-a7d1-116edfe42b48_Screenshot-2026-04-27-134053.png?v=e342e4f583d18517e22766607e8d913b"
              alt="AceAge-Music"
              className="h-20 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Back
          </Link>
        </div>
      </div>

      {/* Page header */}
      <div className="relative z-10 pt-40 pb-10 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-10 h-px bg-white/30" />
            <span className="text-white/40 text-xs font-medium tracking-[0.3em] uppercase">Production Catalog</span>
            <span className="w-10 h-px bg-white/30" />
          </div>

          <div className="flex flex-col items-center gap-2 mb-4">
            <div>
              <h1
                className="font-bold leading-tight mb-1"
                style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontFamily: "'Arial Black', 'Impact', 'Franklin Gothic Medium', sans-serif", fontStyle: 'italic', letterSpacing: '0.01em' }}
              >
                <span style={{ color: '#ffffff', textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.5)' }}>AceAge-</span>
                <span style={{ color: '#00c2d4', textShadow: '2px 2px 12px rgba(0,180,210,0.4), 0 0 30px rgba(0,180,210,0.2)' }}>Music</span>
              </h1>
              <p className="text-white/30 text-sm tracking-widest uppercase">Full catalog listing — aliases, production credits, and remix work</p>
            </div>
          </div>

          <div className="h-px bg-white/8 mb-10" />

          <div className="flex flex-wrap justify-center gap-8 mb-12">
            {sections.map(s => (
              <div key={s.key} className="flex flex-col gap-1">
                <span className="text-white font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', letterSpacing: '0.05em' }}>
                  {s.data.length}
                </span>
                <span className="text-white/30 text-[10px] tracking-widest uppercase">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 pb-32 px-6">
        <div className="max-w-7xl mx-auto">

          {/* Search on top, tabs below — centered as a block */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex flex-col gap-3">
              {/* Search row */}
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm w-4 h-4 flex items-center justify-center" />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>
              {/* Tabs row */}
              <div className="flex items-center gap-1 bg-white/5 rounded-full px-1 py-1">
                {sections.map(s => (
                  <button
                    key={s.key}
                    onClick={() => { setActiveSection(s.key); setSearch(''); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium tracking-widest uppercase transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSection === s.key ? 'bg-white text-black' : 'text-white/40 hover:text-white'
                    }`}
                  >
                    <i className={`${s.icon} w-3 h-3 flex items-center justify-center`} />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search results across all categories */}
          {isSearching ? (
            <>
              <div className="max-w-4xl mx-auto flex items-center gap-4 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/8">
                  <i className="ri-search-line text-white/60 text-sm w-4 h-4 flex items-center justify-center" />
                </div>
                <h2 className="text-white/80 font-semibold tracking-widest uppercase text-sm">Search Results</h2>
                <span className="text-white/20 text-xs tracking-widest">— {totalResults} entries across all categories</span>
              </div>
              {totalResults === 0 ? (
                <div className="py-20 text-center text-white/20 text-xs tracking-widest uppercase">No results found</div>
              ) : (
                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 rounded-lg overflow-hidden border border-white/5">
                  {allSearchResults.map((r, i) => (
                    <SearchResultEntry key={`${r.section}-${r.item}`} item={r.item} index={i} sectionLabel={r.sectionLabel} sectionIcon={r.sectionIcon} />
                  ))}
                </div>
              )}
              <div className="max-w-4xl mx-auto mt-8 flex items-center justify-center gap-3">
                <span className="w-10 h-px bg-white/10" />
                <span className="text-white/20 text-xs tracking-widest uppercase">{totalResults} results found</span>
                <span className="w-10 h-px bg-white/10" />
              </div>
            </>
          ) : (
            <>
              {filtered.length === 0 ? (
                <div className="py-20 text-center text-white/20 text-xs tracking-widest uppercase">No results found</div>
              ) : (
                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 rounded-lg overflow-hidden border border-white/5">
                  {filtered.map((item, i) => (
                    <CatalogEntry key={item} item={item} index={i} section={activeSection} />
                  ))}
                </div>
              )}
              <div className="max-w-4xl mx-auto mt-8 flex items-center justify-center gap-3">
                <span className="w-10 h-px bg-white/10" />
                <span className="text-white/20 text-xs tracking-widest uppercase">
                  {filtered.length} of {active.data.length} entries shown
                </span>
                <span className="w-10 h-px bg-white/10" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const sectionAccents: Record<Section, string> = {
  aliases: 'text-amber-400/70',
  produced: 'text-emerald-400/70',
  remixes: 'text-rose-400/70',
};

function CatalogEntry({ item, index, section }: { item: string; index: number; section: Section }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`bg-[#0e0e0e] border border-white/5 px-6 h-16 flex items-center justify-between group transition-colors duration-200 ${hovered ? 'bg-[#141414]' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center gap-4">
        <span className="text-white/15 text-xs font-bold w-5 text-right flex-shrink-0" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span
          className={`font-normal tracking-wider uppercase transition-colors duration-200 ${hovered ? 'text-white' : 'text-white/70'}`}
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em', fontSize: '1.1rem' }}
        >
          {item}
        </span>
      </div>
    </div>
  );
}

function SearchResultEntry({ item, index, sectionLabel, sectionIcon }: { item: string; index: number; sectionLabel: string; sectionIcon: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`bg-[#0e0e0e] border border-white/5 px-6 h-16 flex items-center justify-between group transition-colors duration-200 ${hovered ? 'bg-[#141414]' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center gap-4">
        <span className="text-white/15 text-xs font-bold w-5 text-right flex-shrink-0" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span
          className={`font-normal tracking-wider uppercase transition-colors duration-200 ${hovered ? 'text-white' : 'text-white/70'}`}
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em', fontSize: '1.1rem' }}
        >
          {item}
        </span>
      </div>
      <div className="flex items-center gap-1 text-white/20">
        <i className={`${sectionIcon} text-xs`} />
      </div>
    </div>
  );
}
